import type { ReactNode } from "react";
import { portalApi, type PortalApiClient } from "./api/client";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { AppShell } from "./components/AppShell";
import { LoadingState } from "./components/Shared";
import {
  AccountsPage,
  AuditPage,
  NoRoutePage,
  NotAllowedPage,
  OfflinePage,
  SecurityPage,
  SystemPage,
  WorkersPage,
} from "./pages/AdminPages";
import { ChangePasswordPage, LoginPage, TotpSetupPage } from "./pages/AuthPages";
import { DashboardPage } from "./pages/DashboardPage";
import { IdeasPage } from "./pages/IdeasPage";
import { JobDetailPage } from "./pages/JobDetailPage";
import { ProfilesPage } from "./pages/ProfilesPage";
import { UploadsPage } from "./pages/UploadsPage";
import { WorkPage } from "./pages/WorkPage";
import { Navigate, RouterProvider, useRouter } from "./routing/Router";
import type { Role } from "./types";

export function App({ client = portalApi }: { client?: PortalApiClient }) {
  return (
    <RouterProvider>
      <AuthProvider client={client}>
        <PortalRoutes client={client} />
      </AuthProvider>
    </RouterProvider>
  );
}

function PortalRoutes({ client }: { client: PortalApiClient }) {
  const { path } = useRouter();
  const { session, state } = useAuth();
  const pathname = path.split("?")[0];

  if (state === "loading") return <LoadingState label="Opening SwipeCut" />;
  if (state === "offline") return <OfflinePage />;

  if (!session) {
    return pathname === "/login" ? <LoginPage /> : <Navigate to="/login" />;
  }
  if (session.user.mustChangePassword) {
    return pathname === "/change-password" ? <ChangePasswordPage /> : <Navigate to="/change-password" />;
  }
  if (session.user.role === "owner" && session.totpEnrollmentRequired) {
    return pathname === "/setup-authenticator" ? (
      <TotpSetupPage client={client} />
    ) : (
      <Navigate to="/setup-authenticator" />
    );
  }
  if (["/login", "/change-password", "/setup-authenticator"].includes(pathname)) {
    return <Navigate to={session.user.role === "owner" ? "/" : "/work"} />;
  }

  const content = resolveAuthenticatedRoute(pathname, session.user.role, client);
  return <AppShell>{content}</AppShell>;
}

function resolveAuthenticatedRoute(pathname: string, role: Role, client: PortalApiClient): ReactNode {
  if (pathname === "/") {
    return role === "owner" || role === "manager" ? (
      <DashboardPage client={client} />
    ) : (
      <Navigate to="/work" />
    );
  }
  if (pathname === "/work") return <WorkPage client={client} />;
  if (pathname.startsWith("/jobs/")) return <JobDetailPage client={client} />;
  if (pathname === "/ideas") {
    return gate(role, ["owner", "ideation", "manager"], <IdeasPage client={client} />);
  }
  if (pathname === "/profiles") {
    return gate(role, ["owner", "manager"], <ProfilesPage client={client} />);
  }
  if (pathname === "/uploads") {
    return gate(role, ["owner", "uploader"], <UploadsPage client={client} />);
  }
  if (pathname === "/workers") {
    return gate(role, ["owner"], <WorkersPage client={client} />);
  }
  if (pathname === "/accounts") {
    return gate(role, ["owner"], <AccountsPage client={client} />);
  }
  if (pathname === "/system") {
    return gate(role, ["owner"], <SystemPage client={client} />);
  }
  if (pathname === "/audit") {
    return gate(role, ["owner"], <AuditPage client={client} />);
  }
  if (pathname === "/security") {
    return gate(role, ["owner"], <SecurityPage client={client} />);
  }
  return <NoRoutePage />;
}

function gate(role: Role, roles: Role[], content: ReactNode): ReactNode {
  return roles.includes(role) ? content : <NotAllowedPage />;
}
